/**
 * Branding API Service for Wadakaroo
 *
 * Manages the organisation's custom logo, used on invoice PDFs and customer
 * emails. Uploading is allowed on any plan; the logo is only rendered while the
 * plan enables the custom-branding feature (Enterprise) — the `customBrandingActive`
 * flag on the response says whether it is currently in effect.
 */

import axiosInstance from './axiosInstance';

/**
 * Get the current branding logo metadata, or null if none is set.
 * @returns {Promise<null | {
 *   uuid: string,
 *   originalFilename: string,
 *   contentType: string,
 *   fileSize: number,
 *   url: string,
 *   customBrandingActive: boolean
 * }>}
 */
export const getBrandingApi = async () => {
    const response = await axiosInstance.get('/organisation/branding/logo');
    // Backend returns 204 No Content when no logo has been uploaded.
    if (response.status === 204 || !response.data) {
        return null;
    }
    return response.data;
};

/**
 * Upload (or replace) the organisation's logo.
 * @param {File} file - the image file (jpeg/png/gif/webp, max 5 MB)
 * @returns {Promise<Object>} the persisted branding metadata
 */
export const uploadBrandingLogoApi = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post('/organisation/branding/logo', form, {
        // ⚠️ NAMED, not undefined. A per-request `undefined` does NOT delete an
        // instance default — axios skips it on merge — so the default
        // application/json stood, axios flattened the FormData to JSON, and the
        // server answered 415. The comment here used to claim otherwise.
        //
        // Naming multipart makes hasJSONContentType false so the FormData passes
        // through; the xhr adapter then replaces this value with the browser's own,
        // boundary and all.
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

/**
 * Remove the organisation's logo.
 * @returns {Promise<void>}
 */
export const deleteBrandingLogoApi = async () => {
    await axiosInstance.delete('/organisation/branding/logo');
};

const brandingApi = {
    getBrandingApi,
    uploadBrandingLogoApi,
    deleteBrandingLogoApi,
};

export default brandingApi;
